import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import UsersDrawer from '../UsersDrawer';
import {
  Button,
  FormControl,
  TextField,
  CircularProgress,
} from '@material-ui/core';
import { FlexBox } from '../../../global/FlexBox';
import { useCreateOrganization } from '../../hooks';
import { CREATE_ORG_DRAWER_ID, INPUT_MAX_LENGTH } from '../../constants';
import { useMinimizedDrawers } from '../../context/MinimizedDrawersContext';
import { useUserManagementContext } from '../../context/UserManagementContext';
import { enqueueSnackbar } from 'notistack';
import {
  organizationValidationSchema,
  OrganizationFormData,
} from '../../validationSchemas';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { TeamAdminsField } from './TeamAdmins/TeamAdminsField';

const DEFAULT_FORM_DATA: OrganizationFormData = {
  name: '',
  description: '',
  orgAdminEmails: [],
};

export const CreateOrgDrawer = () => {
  const {
    isCreateOrganisationsDrawerOpen,
    setIsCreateOrganisationsDrawerOpen,
    createOrgData,
  } = useUserManagementContext();

  const {
    mutate: createOrg,
    isPending,
    reset: resetMutation,
  } = useCreateOrganization();
  const { minimizeDrawer } = useMinimizedDrawers();

  const { control, handleSubmit, setValue, getValues, reset, clearErrors } =
    useForm<OrganizationFormData>({
      resolver: yupResolver(organizationValidationSchema),
      defaultValues: DEFAULT_FORM_DATA,
    });

  const resetForm = () => {
    reset();
    resetMutation();
  };

  const handleDrawerClose = () => {
    resetForm();
    setIsCreateOrganisationsDrawerOpen(false);
  };

  const onSubmit = async (data: OrganizationFormData) => {
    clearErrors();

    createOrg(data, {
      onSuccess: () => {
        setIsCreateOrganisationsDrawerOpen(false);
        enqueueSnackbar('Team created successfully', {
          variant: 'success',
        });
      },
      onError: error => {
        enqueueSnackbar(getApiErrorMessage(error, 'Failed to create team'), {
          variant: 'error',
        });
      },
    });
  };

  const handleCreateOrg = () => {
    handleSubmit(onSubmit)();
  };

  const handleCreateAnotherOrg = async () => {
    const data = getValues();

    clearErrors();

    createOrg(data, {
      onSuccess: () => {
        resetForm();
        enqueueSnackbar('Team created successfully', {
          variant: 'success',
        });
      },
      onError: error => {
        enqueueSnackbar(getApiErrorMessage(error, 'Failed to create team'), {
          variant: 'error',
        });
      },
    });
  };

  useEffect(() => {
    if (createOrgData) {
      setValue('name', createOrgData.name || '');
      setValue('description', createOrgData.description || '');
    }
  }, [createOrgData, setValue]);

  const handleHide = () => {
    const formData = getValues();
    minimizeDrawer(CREATE_ORG_DRAWER_ID, 'Create Team', formData);
    handleDrawerClose();
  };

  return (
    <UsersDrawer
      id="create-org-drawer"
      title="Add New Team"
      hasHide
      handleDrawerClose={handleDrawerClose}
      isOpen={isCreateOrganisationsDrawerOpen}
      confirmOnClose
      confirmTitle="Are you sure you want to cancel?"
      confirmContent="All the details provided will be lost."
      confirmButtonText="Cancel Adding Team"
      cancelButtonText="Dismiss"
      handleHide={handleHide}
    >
      <FormControl
        style={{
          display: 'flex',
          width: '100%',
          gap: 32,
          flexDirection: 'column',
          marginBottom: 24,
        }}
      >
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="Team Name"
              required
              label="Team Name"
              InputLabelProps={{ shrink: true }}
              error={!!error}
              helperText={error?.message}
              disabled={isPending}
              inputProps={{
                maxLength: INPUT_MAX_LENGTH,
              }}
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="Team Description"
              label="Description"
              InputLabelProps={{ shrink: true }}
              error={!!error}
              helperText={error?.message}
              disabled={isPending}
              inputProps={{
                maxLength: INPUT_MAX_LENGTH,
              }}
            />
          )}
        />

        <Controller
          name="orgAdminEmails"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TeamAdminsField
              value={field.value || []}
              onChange={field.onChange}
              disabled={isPending}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormControl>
      <FlexBox sx={{ justifyContent: 'flex-end', gridColumnGap: 24 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateOrg}
          disabled={isPending}
        >
          {isPending ? (
            <FlexBox sx={{ alignItems: 'center' }}>
              <CircularProgress
                size={20}
                color="inherit"
                style={{ marginRight: 8 }}
              />
              Creating...
            </FlexBox>
          ) : (
            'Create Team'
          )}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateAnotherOrg}
          disabled={isPending}
        >
          {isPending ? (
            <FlexBox sx={{ alignItems: 'center' }}>
              <CircularProgress
                size={20}
                color="inherit"
                style={{ marginRight: 8 }}
              />
              Creating...
            </FlexBox>
          ) : (
            'Save and Create Another'
          )}
        </Button>
      </FlexBox>
    </UsersDrawer>
  );
};
